import { BulkTagItemType, Partner } from '@/types/partners';

export type PartnerWithTagsType = Pick<Partner, 'uuid' | 'tags'>;

export type TagEditType = { removeLabels: string[]; addLabels: string[] };

function labelsOf(partner: PartnerWithTagsType) {
  return partner.tags.map((tag) => tag.label);
}

/**
 * Labels carried by every partner.
 *
 * @param partners - The partners selected when the modal opened
 * @returns The shared labels, in the first partner's order
 */
export function commonTagLabels(partners: PartnerWithTagsType[]): string[] {
  const [first, ...rest] = partners;
  if (!first) return [];
  const labelSets = rest.map((partner) => new Set(labelsOf(partner)));
  return labelsOf(first).filter((label) =>
    labelSets.every((labels) => labels.has(label)),
  );
}

/**
 * Builds the bulk tag save payload. Each item replaces that partner's whole
 * tag set, so it keeps the tags the modal never showed.
 *
 * @param partners - The partners selected when the modal opened
 * @param edit - Labels removed from and added to every partner
 * @returns One item per partner, in input order
 */
export function buildBulkTagItems(
  partners: PartnerWithTagsType[],
  edit: TagEditType,
): BulkTagItemType[] {
  const removedLabels = new Set(edit.removeLabels);
  return partners.map((partner) => {
    const keptLabels = labelsOf(partner).filter(
      (label) => !removedLabels.has(label),
    );
    return {
      partner_organization_uuid: partner.uuid,
      labels: [...new Set([...keptLabels, ...edit.addLabels])],
    };
  });
}
